import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Clarity from '@microsoft/clarity';
import { capturePosthogEvent } from '../../../utils/posthog';
import { HiOutlinePlus, HiOutlineUserGroup } from 'react-icons/hi';
import { IoMdInformationCircleOutline } from 'react-icons/io';
import { CgImage } from 'react-icons/cg';
import { FiDownload } from 'react-icons/fi';
import { MdOutlinePhotoCamera } from 'react-icons/md';
import { LuCopy } from 'react-icons/lu';
import { RiDeleteBinLine } from 'react-icons/ri';
import { editorStore } from '../../../store/editorStore';
import { floorplannerStore } from '../../../store/floorplannerStore';
import { renderStore } from '../../../store/renderStore';
import { userStore } from '../../../store/userStore';
import { canShareTeam } from '../../../utils/canShareTeam';
import ConfirmDeletePopup from '../ConfirmDeletePopup';
import ShareFloorplanModal from '../ShareFloorplanModal';
import { DisplayLengthUnitToggle } from '../../DisplayLengthUnitToggle';
import type { PropertyPanelController } from './types';
import { observer } from 'mobx-react-lite';

type PropertyPanelHeaderProps = Pick<
  PropertyPanelController,
  'optionMenuOpen' | 'setOptionMenuOpen' | 'optionMenuRef' | 'floorplans' | 'isDeletePopupOpen' | 'setIsDeletePopupOpen'
>;

const PropertyPanelHeader: React.FC<PropertyPanelHeaderProps> = observer(({
  optionMenuOpen,
  setOptionMenuOpen,
  optionMenuRef,
  floorplans,
  isDeletePopupOpen,
  setIsDeletePopupOpen,
}) => {
  const { t } = useTranslation();
  const [rendering, setRendering] = useState(false);
  const [infoPopupOpen, setInfoPopupOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const ownedFloorplanCount = floorplans.filter((fp) => fp.isOwner !== false).length;
  const showShare =
    canShareTeam() &&
    floorplannerStore.isOwner &&
    Boolean(floorplannerStore.id);

  return (
    <div className="flex w-full min-w-0 items-center gap-1.5 px-2 pt-2 pb-3 select-none">
      <div className="relative shrink-0">
        <div className="flex h-[34px] w-[34px] items-center justify-center rounded-lg border border-stone-500 p-1 cursor-pointer hover:bg-gray-100" onClick={() => setInfoPopupOpen(!infoPopupOpen)} title={t('propertyPanel.information')}>
          <IoMdInformationCircleOutline style={{ strokeWidth: 2 }} size={19} />
        </div>
        {infoPopupOpen && (
          <div className="fixed right-20 top-16 w-80 bg-white border border-gray-300 shadow-lg rounded-lg py-2 z-50">
            <div className="px-4 py-2">
              <h3 className="font-semibold text-sm mb-2">{t('propertyPanel.title')}</h3>
              <p className="text-xs text-gray-600 mb-2">{t('propertyPanel.infoDescription')}</p>
              <ul className="text-xs text-gray-600 list-disc list-inside space-y-1">
                <li>{t('propertyPanel.wallDimensions')}</li>
                <li>{t('propertyPanel.symbolPropertiesList')}</li>
                <li>{t('propertyPanel.lineColorsWeightsStyles')}</li>
                <li>{t('propertyPanel.fillColors')}</li>
              </ul>
              <p className="text-xs text-gray-600 mt-2">{t('propertyPanel.emptyHint')}</p>
            </div>
          </div>
        )}
      </div>
      <DisplayLengthUnitToggle variant="segmented" className="shrink-0" />
      <div className={`relative flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-lg border border-stone-500 p-1 cursor-pointer ${optionMenuOpen ? 'bg-black text-white' : ''}`} onClick={() => setOptionMenuOpen(!optionMenuOpen)} ref={optionMenuRef}>
        <div className="text-zinc-950 font-['DM Sans'] pb-1 text-center text-xl font-normal">···</div>
        {optionMenuOpen && (
          <div className="fixed right-20 top-16 w-auto bg-black text-white border border-gray-300 shadow-lg rounded-lg py-2 z-20">
            <div
              className="px-4 py-2 cursor-pointer flex items-start gap-2 flex-row hover:bg-[#66645F]"
              onClick={() => {
                if (userStore.isFreemiumUser) {
                  setOptionMenuOpen(false);
                  editorStore.showPaywall('addToProject');
                  return;
                }
                editorStore.clearSelections();
                editorStore.openAddToPresentationPopup();
              }}
            >
              <CgImage className="text-xl transform scale-90" />
              {t('propertyPanel.addToProject')}
            </div>
            <div className="border-b border-[#66645F]" />
            {showShare && (
              <>
                <div
                  className="px-4 py-2 cursor-pointer flex items-start gap-2 flex-row hover:bg-[#66645F]"
                  onClick={() => {
                    setOptionMenuOpen(false);
                    setShareModalOpen(true);
                  }}
                >
                  <HiOutlineUserGroup className="text-xl transform scale-90" />
                  {t('propertyPanel.shareWithTeam')}
                </div>
                <div className="border-b border-[#66645F]" />
              </>
            )}
            {editorStore.view3D && (
              <>
                <div
                  className="px-4 py-2 cursor-pointer flex items-start gap-2 flex-row hover:bg-[#66645F]"
                  onClick={() => {
                    editorStore.openRender3DViewModal();
                    setOptionMenuOpen(false);
                  }}
                >
                  <MdOutlinePhotoCamera className="text-xl transform scale-90" />
                  {t('propertyPanel.renderPhoto')}
                </div>
                <div className="border-b border-[#66645F]" />
              </>
            )}
            <div
              className="px-4 py-2 cursor-pointer flex items-start gap-2 flex-row hover:bg-[#66645F]"
              onClick={() => {
                if (userStore.isFreemiumUser) {
                  setOptionMenuOpen(false);
                  editorStore.showPaywall('download');
                  return;
                }
                editorStore.clearSelections();
                editorStore.openDownloadProjectPopup();
              }}
            >
              <FiDownload className="text-xl transform scale-90" />
              {t('propertyPanel.downloadProject')}
            </div>
            <div className="border-b border-[#66645F]" />
            <div
              className="px-4 py-2 cursor-pointer flex items-start gap-2 flex-row hover:bg-[#66645F]"
              onClick={() => {
                const limit = userStore.maxFloorplans;
                if (limit != null && ownedFloorplanCount >= limit) {
                  setOptionMenuOpen(false);
                  editorStore.showPaywall('project');
                  return;
                }
                Clarity.event('new_project');
                capturePosthogEvent('new_project');
                window.location.href = '/floorplan/';
              }}
            >
              <HiOutlinePlus className="text-xl transform scale-90" />
              {t('propertyPanel.createNewProject')}
            </div>
            <div
              className="px-4 py-2 cursor-pointer flex items-start gap-2 flex-row hover:bg-[#66645F]"
              onClick={() => {
                const limit = userStore.maxFloorplans;
                if (limit != null && ownedFloorplanCount >= limit) {
                  setOptionMenuOpen(false);
                  editorStore.showPaywall('project');
                  return;
                }
                Clarity.event('duplicate_project');
                capturePosthogEvent('duplicate_project');
                floorplannerStore.duplicateProject();
              }}
            >
              <LuCopy className="text-xl transform scale-90" />
              {t('propertyPanel.copyProject')}
            </div>
            {floorplannerStore.isOwner && (
              <div
                className="px-4 py-2 cursor-pointer flex items-start gap-2 flex-row hover:bg-[#66645F]"
                onClick={() => {
                  setIsDeletePopupOpen(true);
                  setOptionMenuOpen(false);
                }}
              >
                <RiDeleteBinLine className="text-xl transform scale-90" />
                {t('propertyPanel.deleteProject')}
              </div>
            )}
          </div>
        )}
      </div>
      <div
        className={
          'flex h-[34px] min-w-0 flex-1 items-center justify-center truncate rounded-lg px-2 cursor-pointer' +
          (floorplannerStore.dirty && !floorplannerStore.lockedByOther
            ? ' bg-boniYellowPrimary text-blackplum'
            : ' bg-eggshell text-gray-900') +
          (floorplannerStore.lockedByOther ? ' opacity-50 cursor-not-allowed' : '')
        }
        onClick={() => {
          if (floorplannerStore.lockedByOther) return;
          setRendering(true);
          if (editorStore.camera) {
            if (editorStore.view3D) {
              renderStore.capture3DSnapshot('upload', undefined, editorStore.camera);
            } else {
              renderStore.captureSnapshot('upload', undefined, undefined, editorStore.camera);
            }
          }
          floorplannerStore.saveToCloud();
          setTimeout(() => {
            setRendering(false);
          }, 200);
        }}
      >
        {rendering ? (
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900" />
        ) : (
          <span className="truncate font-['DM Sans'] text-center text-sm font-normal">
            {floorplannerStore.lockedByOther
              ? t('propertyPanel.locked')
              : floorplannerStore.dirty
                ? t('propertyPanel.save')
                : t('propertyPanel.saved')}
          </span>
        )}
      </div>
      {isDeletePopupOpen && (
        <ConfirmDeletePopup
          onConfirm={() => {
            floorplannerStore.deleteFromCloud(floorplannerStore.id);
            setIsDeletePopupOpen(false);
          }}
          onCancel={() => setIsDeletePopupOpen(false)}
        />
      )}
      {shareModalOpen && (
        <ShareFloorplanModal
          show={shareModalOpen}
          onClose={() => setShareModalOpen(false)}
        />
      )}
    </div>
  );
});

export default PropertyPanelHeader;
